import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { Subject } from 'rxjs/internal/Subject';

import { SsModule, User } from '@app/models';
import {
  AccountService,
  GenericModuleService,
  LeadService,
  SharedUtilsService,
  SsModuleService,
  TableService,
} from '@app/services';

import { ConfirmActionDialogComponent } from '@app/modules/dialogs/confirm-action-dialog/confirm-action-dialog.component';

const SELECT_ALL_CONFIRM_THRESHOLD = 500;

@Component({
  selector: 'module-search-select',
  templateUrl: './module-search-select.component.html',
  styleUrls: ['./module-search-select.component.scss'],
  standalone: false,
})
export class ModuleSearchSelectComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();

  // Inputs from caller (set on the dialog instance) -----------------------
  fieldToGetRelation: any = null;
  multiSelectMode = false;
  disableSelectBtnOnEmptyValues = false;
  defaultSearchTermsPassed: { key: string; value: any }[] = [];
  allowedSearchFields: string[] = [];
  coreModule: SsModule = null;
  currentRecord: any = null;
  includeProviderUserTypes = false;

  // Legacy compatibility fields — declared but unused. Many callers set
  // these on the instance; keeping them on the public API to avoid breaking
  // the 11+ call sites until each is migrated.
  searchFunction: any;
  columns: any[] = [];
  itemsPerPage = 25;
  isLoadingResults = false;

  // Legacy: callers set this to pre-populate selections before the dialog opens.
  // Synced into _tableService.selectedRows in ngOnInit so the rest of the
  // component (which now reads from _tableService) sees them.
  selectedItems: any[] = [];

  // Resolved module + layout ---------------------------------------------
  moduleToSearch: SsModule = null;
  searchFields: any[] = [];
  gridColumns: any[] = [];

  // Search state ----------------------------------------------------------
  searchTerms: any = {};
  searchOptions = {
    name: '',
    schema: {},
    fields: null as any,
    relationshipsNeeded: null as any,
    searchTerms: {} as any,
  };

  // Pagination + sort -----------------------------------------------------
  page = 0;
  pageSize = 25;
  pageSizeOptions = [25, 50, 100];
  currentSortField: string | null = null;
  currentSortDirection: 'asc' | 'desc' | null = null;

  // Results ---------------------------------------------------------------
  data: any[] = [];
  isLoading = false;
  hasSearched = false;
  isSelectingAll = false;

  // UI state --------------------------------------------------------------
  searchCollapsed = false;

  currentUser: User;

  constructor(
    public _tableService: TableService,
    private _sharedService: SharedUtilsService,
    private _genericService: GenericModuleService,
    private _leadsService: LeadService,
    private _accountService: AccountService,
    private _moduleService: SsModuleService,
    private _matDialog: MatDialog,
    public dialogRef: MatDialogRef<ModuleSearchSelectComponent>,
  ) {}

  ngOnInit() {
    // Override caller-supplied width/height so the redesigned modal renders consistently
    // across all 11+ call sites (most pass width: '95%', which is too wide on big screens).
    this.dialogRef.updateSize('min(960px, calc(100vw - 32px))', 'min(720px, calc(100vh - 64px))');
    this._tableService.resetSelectedRows();
    if (this.selectedItems?.length) {
      this._tableService.setResults(this.selectedItems);
    }
    this.getRelatedModule();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  // Module resolution -----------------------------------------------------
  async getRelatedModule() {
    if (!this.fieldToGetRelation) {
      this.cancel();
      return;
    }

    let moduleName = this.fieldToGetRelation?.type?.schemaType?.name ?? this.fieldToGetRelation.ref;

    if (!moduleName) {
      const modulesInvolved = this.fieldToGetRelation.fieldName?.split('_') ?? [];
      if (this.coreModule) {
        moduleName = modulesInvolved.find((m: string) => m !== this.coreModule.name);
      }
    }

    if (!moduleName) {
      this._sharedService.showErrorDialog('No module to search detected.');
      this.cancel();
      return;
    }

    this.moduleToSearch = await this._moduleService.getFullModuleByNameWithRefresh(moduleName);
    if (!this.moduleToSearch) {
      this._sharedService.showErrorDialog('No module to search detected.');
      this.cancel();
      return;
    }

    this.searchOptions.name = this.moduleToSearch.name;
    this.searchOptions.schema = this.moduleToSearch.customSchema;

    const searchableFields = this.moduleToSearch.fields ?? [];
    if (this.moduleToSearch.relationships) {
      this.moduleToSearch.relationships
        .filter(_f => _f.related_module && _f.related_module.name === this.moduleToSearch.name)
        .forEach(_r => {
          searchableFields.push({ fieldName: _r.name, type: { inputType: 'Relate', schemaType: 'Relate' } });
          this.searchOptions.schema[_r.name] = { type: 'Object', default: '', required: false, ref: null };
        });
    }
    this.searchTerms['fields'] = searchableFields;

    this.getSearchFields();
    this.setDefaultSearchTerms();
    this.getTableLayout();
    this.preselectFromCurrentRecord();
  }

  private getSearchFields() {
    const layout = this.moduleToSearch?.layouts?.find(l => l.view === 'popup-search' && l.app === 'YeeHro CRM');
    if (!layout) {
      this._sharedService.showErrorDialog('No popup-search layout detected for ' + this.moduleToSearch.label);
      return;
    }
    let fields = (layout.sections?.[0]?.sectionFields ?? []).filter((f: any) => f && f.fieldName);
    if (this.allowedSearchFields?.length) {
      fields = fields.filter((f: any) => this.allowedSearchFields.includes(f.fieldName));
    }
    this.searchFields = fields;
  }

  private getTableLayout() {
    const layout = this.moduleToSearch?.layouts?.find(l => l.view === 'popup-listview' && l.app === 'YeeHro CRM');
    if (!layout?.sections?.length) {
      this._sharedService.showErrorDialog('No Popup Listview was found for: ' + this.moduleToSearch.label);
      this.dialogRef.close(null);
      return;
    }
    this.gridColumns = layout.sections[0].sectionFields ?? [];
    this.searchOptions.relationshipsNeeded = this.gridColumns.filter(c => c?.type?.inputType === 'Relate');
  }

  private setDefaultSearchTerms() {
    if (this.defaultSearchTermsPassed?.length) {
      for (const { key, value } of this.defaultSearchTermsPassed) {
        if (key) this.searchTerms[key] = value;
      }
    }

    if (this.moduleToSearch?.name === 'users') {
      this.searchTerms['active'] = true;
      this.searchTerms['userType'] = ['System Admin', 'Standard', 'Vendor'];
      const coreModuleName = this.coreModule?.name ?? '';
      if (this.includeProviderUserTypes || coreModuleName.includes('pto')) this.searchTerms['userType'].push('Provider');
    }

    const fieldName = this.fieldToGetRelation?.fieldName;
    if (fieldName === 'locations_jobs' && this.currentRecord?.accounts_jobs) {
      const accountIds = this.multiSelectMode
        ? [this.currentRecord.accounts_jobs._id ?? this.currentRecord.accounts_jobs]
        : [this.currentRecord.accounts_jobs];
      this.searchTerms['accounts_locations'] = accountIds;
    } else if (fieldName === 'jobs_subs') {
      const profession = this.currentRecord?.leads_subs?.profession ?? null;
      this.searchTerms['profession'] = [profession];
    } else if (fieldName === 'source_contact') {
      this.searchTerms['accounts_contacts'] = [this.currentRecord.accounts_jobs];
    }
  }

  private preselectFromCurrentRecord() {
    const value = this.fieldToGetRelation && this.currentRecord?.[this.fieldToGetRelation.fieldName];
    if (Array.isArray(value)) {
      value.filter(v => v).forEach(v => this._tableService.toggleItem(v));
    }
  }

  // Search ----------------------------------------------------------------
  search() {
    this.page = 0;
    this.getPageData();
  }

  clear() {
    this.searchTerms = {};
    this.searchTerms['fields'] = this.moduleToSearch?.fields ?? [];
    this.setDefaultSearchTerms();
    this.search();
  }

  clearFilter(key: string) {
    delete this.searchTerms[key];
    this.search();
  }

  relatedFieldChosen(relatedFieldObj: any) {
    const _field = relatedFieldObj.field;
    const _chosenSelection = relatedFieldObj.chosenRecord;
    let sourceField = _field?.type?.schemaType?.name ?? _field?.fieldName;
    if (!_chosenSelection.name) {
      _chosenSelection.name = this._genericService.getNameForModule(_field.ref, _chosenSelection);
    }
    if ((!sourceField || sourceField === 'Object') && _field.fieldName) sourceField = _field.fieldName;
    this.searchTerms[sourceField] = _chosenSelection;
  }

  collapseSearch() { this.searchCollapsed = true; }
  expandSearch()   { this.searchCollapsed = false; }

  // Active filter chips (for collapsed search row) ------------------------
  get activeFilterChips() {
    const skip = new Set(['fields', 'nonCache', 'inclusionSearch', 'searchMode']);
    return Object.entries(this.searchTerms)
      .filter(([key, value]) => !skip.has(key) && this.hasValue(value))
      .map(([key, value]) => ({
        key,
        label: this.searchFields.find(f => f.fieldName === key)?.label ?? key,
        value: this.displayValue(value),
      }));
  }

  private hasValue(v: unknown): boolean {
    if (v == null || v === '') return false;
    if (Array.isArray(v)) return v.length > 0 && v.some(x => x != null && x !== '');
    if (typeof v === 'object') return Object.keys(v as object).length > 0;
    return true;
  }

  private displayValue(v: any): string {
    if (Array.isArray(v)) return v.map(x => x?.name ?? x?.label ?? x).join(', ');
    if (typeof v === 'object') return v.name ?? v.label ?? '(selected)';
    return String(v);
  }

  // Data fetch ------------------------------------------------------------
  private setSearchData() {
    const fieldsToIgnore = ['fields', 'nonCache'];
    const moduleFields = this.moduleToSearch.fields ?? [];
    this.searchOptions.fields = moduleFields;

    const currentSearchMode = this.searchOptions.searchTerms['searchMode'] ?? 'basic';
    this.searchOptions.searchTerms = { fields: moduleFields, searchMode: currentSearchMode };

    const inclusionTerms: any = {};
    Object.keys(this.searchTerms).filter(st => !fieldsToIgnore.includes(st)).forEach(st => {
      inclusionTerms[st] = { inclusionType: 'Includes', value: this.searchTerms[st] };
      this.searchOptions.searchTerms[st] = this.searchTerms[st];
    });
    this.searchOptions.searchTerms['inclusionSearch'] = inclusionTerms;
  }

  async getPageData() {
    this.isLoading = true;
    this.setSearchData();
    this.searchTerms['nonCache'] = true;

    // Service signatures default to 'name' / 'asc' when these are undefined.
    // Don't pass empty strings — they override the defaults and Mongo rejects.
    const sortField = this.currentSortField || 'name';
    const sortDir = this.currentSortDirection || 'asc';

    try {
      let result: any;
      if (this.moduleToSearch.name === 'leads') {
        result = await lastValueFrom(
          this._leadsService.getLeadCustomDatasource(this.searchOptions.searchTerms, sortField, sortDir, this.page, this.pageSize),
        );
      } else if (this.moduleToSearch.name === 'accounts') {
        result = await lastValueFrom(
          this._accountService.getCustomDatasource(this.searchOptions.searchTerms, sortField, sortDir, this.page, this.pageSize),
        );
      } else {
        result = await lastValueFrom(
          this._genericService.getDataSource(this.searchOptions, sortField, sortDir, this.page, this.pageSize),
        );
      }

      this.data = result?.message ?? [];
      this.hasSearched = true;

      // The leads datasource defers the count for performance (returns null so rows
      // paint immediately) — accounts/generic return it inline. Fall back to the rows
      // we have so they still render, then resolve the real total separately.
      const serverCount = result?.totalItemCount;
      this._tableService.dataLength = serverCount ?? this.data.length;
      if (serverCount == null && this.moduleToSearch.name === 'leads') {
        this.resolveDeferredLeadCount(sortField, sortDir);
      }
    } catch (err) {
      console.log('Error getting grid results: ', err);
      this.data = [];
    } finally {
      this.isLoading = false;
    }
  }

  // Leads' dataSourceSearch returns totalItemCount: null (count deferred to keep the
  // multi-million-doc grid responsive). Fetch it here so the "X found" header, paginator,
  // and "Select all (N)" reflect the full match set rather than just the current page.
  private async resolveDeferredLeadCount(sortField: string, sortDir: string) {
    const pageSnapshot = this.page;
    try {
      const countResult: any = await lastValueFrom(
        this._leadsService.getLeadCount(this.searchOptions.searchTerms, sortField, sortDir, this.page, this.pageSize),
      );
      // Ignore stale responses if the user has since paged or re-searched.
      if (this.page === pageSnapshot) {
        this._tableService.dataLength = countResult?.totalItemCount ?? this.data.length;
      }
    } catch (err) {
      console.log('Error getting lead count: ', err);
    }
  }

  // Sort ------------------------------------------------------------------
  onSortClick(col: any) {
    if (!col?.fieldName) return;
    const key = col.fieldName;
    if (this.currentSortField === key) {
      this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : null;
      if (!this.currentSortDirection) this.currentSortField = null;
    } else {
      this.currentSortField = key;
      this.currentSortDirection = 'asc';
    }
    this.page = 0;
    this.getPageData();
  }

  sortAttr(col: any): 'asc' | 'desc' | null {
    return col?.fieldName && this.currentSortField === col.fieldName ? this.currentSortDirection : null;
  }

  // Pagination ------------------------------------------------------------
  get totalResults(): number { return this._tableService.dataLength ?? 0; }
  get rangeStart(): number { return this.totalResults === 0 ? 0 : this.page * this.pageSize + 1; }
  get rangeEnd(): number { return Math.min((this.page + 1) * this.pageSize, this.totalResults); }
  get hasNextPage(): boolean { return this.rangeEnd < this.totalResults; }

  prevPage() { if (this.page > 0) { this.page--; this.getPageData(); } }
  nextPage() { if (this.hasNextPage) { this.page++; this.getPageData(); } }

  onPageSizeChange() {
    this.page = 0;
    this.getPageData();
  }

  // Selection -------------------------------------------------------------
  get selectedCount(): number { return this._tableService.selectedRows?.selected?.length ?? 0; }

  toggleRow(row: any) {
    this._tableService.toggleItem(row);
  }

  onRowClick(row: any) {
    if (this.multiSelectMode) this.toggleRow(row);
    else this.dialogRef.close(row);
  }

  toggleAllOnPage() {
    if (this.allOnPageSelected) {
      this.data.forEach(row => {
        if (this._tableService.isItemSelected(row._id)) this._tableService.toggleItem(row);
      });
    } else {
      this.data.forEach(row => {
        if (!this._tableService.isItemSelected(row._id)) this._tableService.toggleItem(row);
      });
    }
  }

  get allOnPageSelected(): boolean {
    return this.data.length > 0 && this.data.every(r => this._tableService.isItemSelected(r._id));
  }

  get someOnPageSelected(): boolean {
    return this.data.some(r => this._tableService.isItemSelected(r._id)) && !this.allOnPageSelected;
  }

  async onSelectAllClick() {
    if (this.totalResults > SELECT_ALL_CONFIRM_THRESHOLD) {
      const confirmed = await this.confirmSelectAll();
      if (!confirmed) return;
    }
    await this.runSelectAll();
  }

  private async confirmSelectAll(): Promise<boolean> {
    const dialogRef = this._matDialog.open(ConfirmActionDialogComponent, { width: '420px' });
    const instance = dialogRef.componentInstance;
    instance.title = `Select all ${this.totalResults.toLocaleString()} ${this.moduleToSearch.label.toLowerCase()}?`;
    instance.textToDisplay = 'This will select every record matching your current filters. Large selections may take a few seconds.';
    instance.items = null;
    instance.buttonsInfo = {
      confirmText: 'Select all',
      confirmBtnColor: 'primary',
      denyText: 'Cancel',
      denyBtnColor: '',
    };
    return (await lastValueFrom(dialogRef.afterClosed())) === true;
  }

  private async runSelectAll() {
    this.isSelectingAll = true;
    try {
      const selectAllSearch = { ...this.searchOptions };
      selectAllSearch.searchTerms = { ...selectAllSearch.searchTerms, selectAll: true };
      await this._tableService.selectAll(selectAllSearch, this._genericService.search(selectAllSearch));
    } finally {
      this.isSelectingAll = false;
    }
  }

  deselectAll() {
    this._tableService.deselectAll();
  }

  // Footer actions --------------------------------------------------------
  confirm() {
    setTimeout(() => this.dialogRef.close(this._tableService.selectedRows.selected), 200);
  }

  cancel() {
    this.dialogRef.close(null);
  }
}
